// src/components/UserLoginForm.jsx
import React, { useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link } from "react-router-dom";
import "../css/UserLogin.css";

const UserLoginForm = () => {
  const [loginRequest, setLoginRequest] = useState({
    emailId: "",
    password: "",
    role: "",
  });

  const CART_KEY = "cart";
  const WISHLIST_KEY = "wishlist";

  const handleUserInput = (e) => {
    const { name, value } = e.target;
    setLoginRequest((prev) => ({ ...prev, [name]: value }));
  };

  // Build payload expected by backend (guest cart)
  const buildGuestCartPayload = (userId) => {
    try {
      const raw = localStorage.getItem(CART_KEY);
      if (!raw) return null;
      const items = JSON.parse(raw);
      const carts = items
        .map((it) => {
          const productId = Number(it.productId);
          const quantity = Number(it.quantity);
          const categorySizeId = it.categorySizeId
            ? Number(it.categorySizeId)
            : 0;
          if (!productId || !quantity || quantity <= 0) return null;
          return { productId, quantity, categorySizeId };
        })
        .filter(Boolean);
      if (carts.length === 0) return null;
      return { userId, carts };
    } catch (err) {
      console.error("Failed to build guest cart payload", err);
      return null;
    }
  };

  // Build payload expected by backend (guest wishlist)
  const buildGuestWishlistPayload = (userId) => {
    try {
      const raw = localStorage.getItem(WISHLIST_KEY);
      if (!raw) return null;
      const items = JSON.parse(raw);
      // map to { userId, productId } entries inside wishlists array
      const wishlists = items
        .map((it) => {
          const productId = Number(
            it.productId ?? it.product?.id ?? it.productId
          );
          if (!productId) return null;
          return { userId, productId };
        })
        .filter(Boolean);
      if (wishlists.length === 0) return null;
      return { userId, wishlists };
    } catch (err) {
      console.error("Failed to build guest wishlist payload", err);
      return null;
    }
  };

  // POST guest cart to backend
  const syncGuestCartToBackend = async (userId, jwtToken) => {
    const payload = buildGuestCartPayload(userId);
    if (!payload) return { success: true, message: "No guest cart to sync" };

    try {
      const resp = await fetch(
        "https://goldenhides.in/api/user/guest/cart/add",
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            ...(jwtToken ? { Authorization: "Bearer " + jwtToken } : {}),
          },
          body: JSON.stringify(payload),
        }
      );
      const body = await resp.json();
      if (resp.ok && body.success) {
        // clear guest cart on successful sync
        localStorage.removeItem(CART_KEY);
        return {
          success: true,
          message: body.responseMessage || "Guest cart synced",
        };
      } else {
        return {
          success: false,
          message: body.responseMessage || "Failed to sync guest cart",
        };
      }
    } catch (err) {
      console.error("syncGuestCartToBackend error", err);
      return { success: false, message: "Error syncing guest cart" };
    }
  };

  // POST guest wishlist to backend
  const syncGuestWishlistToBackend = async (userId, jwtToken) => {
    const payload = buildGuestWishlistPayload(userId);
    if (!payload)
      return { success: true, message: "No guest wishlist to sync" };

    try {
      const resp = await fetch(
        "https://goldenhides.in/api/wishlist/guest/product/add",
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            ...(jwtToken ? { Authorization: "Bearer " + jwtToken } : {}),
          },
          body: JSON.stringify(payload),
        }
      );
      const body = await resp.json();
      if (resp.ok && body.success) {
        localStorage.removeItem(WISHLIST_KEY);
        return {
          success: true,
          message: body.responseMessage || "Guest wishlist synced",
        };
      } else {
        return {
          success: false,
          message: body.responseMessage || "Failed to sync guest wishlist",
        };
      }
    } catch (err) {
      console.error("syncGuestWishlistToBackend error", err);
      return { success: false, message: "Error syncing guest wishlist" };
    }
  };

  const loginAction = async (e) => {
    e.preventDefault();

    if (loginRequest.role === "0" || loginRequest.role === "") {
      toast.error("Select the Role", {
        position: "top-center",
        autoClose: 1000,
      });
      return;
    }

    try {
      const resFetch = await fetch("https://goldenhides.in/api/user/login", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(loginRequest),
      });

      const res = await resFetch.json();

      if (res.success) {
        const userRes = res.users && res.users[0];

        if (userRes?.role === "Admin") {
          sessionStorage.setItem("active-admin", JSON.stringify(userRes));
          sessionStorage.setItem("admin-jwtToken", res.jwtToken);
        } else if (userRes?.role === "Customer") {
          sessionStorage.setItem("active-user", JSON.stringify(userRes));
          sessionStorage.setItem("customer-jwtToken", res.jwtToken);

          // Sync guest cart + wishlist for customers
          try {
            // Attempt cart sync
            const cartSync = await syncGuestCartToBackend(
              userRes.id,
              res.jwtToken
            );
            if (!cartSync.success) {
              // non-blocking: show a warning but continue login
              console.warn("Guest cart sync failed:", cartSync.message);
            }

            // Attempt wishlist sync
            const wishlistSync = await syncGuestWishlistToBackend(
              userRes.id,
              res.jwtToken
            );
            if (!wishlistSync.success) {
              console.warn("Guest wishlist sync failed:", wishlistSync.message);
            }

            // If both succeeded we already cleared localStorage inside each function.
            // If one failed, we kept local data so user can retry from their cart/wishlist page.
          } catch (err) {
            console.error("Error syncing guest data:", err);
            // Inform user but allow login
            toast.warn(
              "Failed to sync guest data. You can retry after login.",
              {
                position: "top-center",
                autoClose: 2000,
              }
            );
          }
        } else if (userRes?.role === "Delivery") {
          sessionStorage.setItem("active-delivery", JSON.stringify(userRes));
          sessionStorage.setItem("delivery-jwtToken", res.jwtToken);
        }

        toast.success(res.responseMessage || "Login successful", {
          position: "top-center",
          autoClose: 1000,
        });

        // redirect after short delay (so user sees toast)
        setTimeout(() => {
          window.location.href = "/home";
        }, 900);
      } else {
        toast.error(res.responseMessage || "Invalid credentials", {
          position: "top-center",
          autoClose: 1200,
        });
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error("It seems server is down", {
        position: "top-center",
        autoClose: 1200,
      });
    }
  };

  return (
    <div className="auth-background">
      <div className="d-flex align-items-center justify-content-center w-100">
        <div className="card form-card text-color custom-bg user-login-card">
          <div className="card-header bg-color custom-bg-text text-center">
            <h4 className="card-title mb-0">User Login</h4>
          </div>

          <div className="card-body">
            <form onSubmit={loginAction} noValidate>
              <div className="mb-3 text-color">
                <label htmlFor="role" className="form-label">
                  <b>User Role</b>
                </label>
                <select
                  id="role"
                  name="role"
                  onChange={handleUserInput}
                  className="form-control"
                  value={loginRequest.role}
                  required
                >
                  <option value="0">Select Role</option>
                  <option value="Admin">Admin</option>
                  <option value="Customer">Customer</option>
                  <option value="Delivery">Delivery Person</option>
                </select>
              </div>

              <div className="mb-3 text-color">
                <label htmlFor="emailId" className="form-label">
                  <b>Email Id</b>
                </label>
                <input
                  id="emailId"
                  name="emailId"
                  type="email"
                  className="form-control"
                  onChange={handleUserInput}
                  value={loginRequest.emailId}
                  required
                />
              </div>

              <div className="mb-3 text-color">
                <label htmlFor="password" className="form-label">
                  <b>Password</b>
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  className="form-control"
                  onChange={handleUserInput}
                  value={loginRequest.password}
                  required
                />
                <div className="mt-2">
                  <Link to="/user/forget-password" className="small text-muted">
                    Forgot password?
                  </Link>
                </div>
              </div>

              <button type="submit" className="btn bg-color custom-bg-text">
                Login
              </button>
            </form>
          </div>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default UserLoginForm;
